import { type ReactNode, useState } from 'react';
import { navItemType, type NavV2Response, type OddsFormat, type SiteConfig } from '@cx-web/domain-types';

import Icon from '../../../../primitives/icon/index';
import { type HeaderNavItemTrackingData } from '../../types';
import { mergePersistentParams } from '../../utils/merge-persistent-params';
import { TertiaryHeaderNavGroup } from '../TertiaryHeaderNavGroup/index';

import Drawer from './sub-components/Drawer/index';
import styles from './HeaderMobile.module.css';

type HeaderMobileProps = {
  navData: NavV2Response;
  oddsFormat: OddsFormat;
  onMenuToggle?: (open: boolean) => void;
  onNavItemClick?: (item: HeaderNavItemTrackingData) => void;
  onOddsFormatChange: (format: OddsFormat) => void;
  onStateChange?: (state: string) => void;
  renderAccountMenu?: (variant: 'desktop' | 'mobile') => ReactNode;
  renderLogo?: ReactNode;
  repubHomeUrl: string;
  selectedState?: string;
  serverUrl: string;
  siteConfig: SiteConfig;
  stateOptions?: Array<{ code: string; label: string }>;
};

export const HeaderMobile = ({
  navData,
  oddsFormat,
  onMenuToggle,
  onNavItemClick,
  onOddsFormatChange,
  onStateChange,
  renderAccountMenu,
  renderLogo,
  repubHomeUrl,
  selectedState,
  serverUrl,
  siteConfig,
  stateOptions,
}: HeaderMobileProps) => {
  const { mobileHamburgerNav } = navData;
  const filteredNavItems = mobileHamburgerNav.filter(
    link =>
      link.type !== navItemType.STATE_SELECTOR &&
      link.type !== navItemType.ODDS_FORMAT &&
      link.type !== navItemType.MY_ACCOUNT,
  );

  const [navDrawerOpen, setNavDrawerOpen] = useState(false);

  const requestUrl = serverUrl;

  const handleNavClick = () => {
    const nextOpen = !navDrawerOpen;
    setNavDrawerOpen(nextOpen);
    onMenuToggle?.(nextOpen);
  };

  const shouldShowMyOC = !!mobileHamburgerNav.find(link => link.type === navItemType.MY_ACCOUNT);
  const showOddsFormat = !!mobileHamburgerNav.find(link => link.type === navItemType.ODDS_FORMAT);

  return (
    <>
      <header className={styles.mobileHeader} data-testid="header-mobile-v2">
        <nav className={`${styles.navBar}`}>
          <div className={styles.burgerLogo}>
            <button data-testid="hamburger-menu" onClick={handleNavClick}>
              <Icon className={styles.burgerIcon} icon="HamburgerMenu" />
            </button>
            <a className={styles.iconWrapper} href={mergePersistentParams(repubHomeUrl, requestUrl)}>
              {renderLogo}
            </a>
          </div>
          {shouldShowMyOC && renderAccountMenu?.('mobile')}
        </nav>
        <div className={styles.subMenu}>
          <TertiaryHeaderNavGroup
            bookmakers={siteConfig.bookmakers}
            serverUrl={serverUrl}
            thirdLevelNav={navData.thirdLevelNav}
          />
        </div>
      </header>
      <Drawer
        firstLevelNav={navData.firstLevelNav}
        isOpen={navDrawerOpen}
        mobileHamburgerNav={mobileHamburgerNav}
        oddsFormat={oddsFormat}
        onNavItemClick={onNavItemClick}
        onOddsFormatChange={onOddsFormatChange}
        onStateChange={onStateChange}
        pages={filteredNavItems}
        path={'/'}
        selectedState={selectedState}
        serverUrl={serverUrl}
        showOddsFormat={showOddsFormat}
        siteConfig={siteConfig}
        stateOptions={stateOptions}
        toggleDrawer={handleNavClick}
      />
    </>
  );
};
